import { Injectable, Signal, signal } from '@angular/core';

/**
 * Controls exposed by the on-screen PDF viewer (DocumentPreviewComponent) so
 * the report toolbar can host the page/zoom controls in its single strip,
 * next to the report selector and the Email/Download/Print buttons.
 */
export interface DocumentViewerController {
  readonly page: Signal<number>;
  readonly pageCount: Signal<number>;
  readonly zoomLabel: Signal<string>;
  setPage(page: number): void;
  previousPage(): void;
  nextPage(): void;
  zoomIn(): void;
  zoomOut(): void;
  fitWidth(): void;

  /**
   * Re-fetches the PDF for the SAME document. Needed because the preview loads on a
   * change of documentType/reportId, and an amend to the record changes neither — so
   * without this the operator keeps looking at the pre-amend document.
   */
  reload(): void;
}

/**
 * Bridges the PDF viewer and the report toolbar. The toolbar stamps the
 * screen's report template with ngTemplateOutlet, so the viewer's DI chain
 * goes to the template's declaration site (the screen), not the toolbar —
 * a component-provided service would never reach it. Root-provided with a
 * single "active viewer" slot instead: only one preview is on screen at a
 * time (one Reports tab, one report selected).
 */
@Injectable({ providedIn: 'root' })
export class DocumentViewerService {
  private readonly activeViewer = signal<DocumentViewerController | undefined>(undefined);

  /** The currently visible viewer, or undefined when no PDF preview is shown. */
  public readonly viewer = this.activeViewer.asReadonly();

  register(viewer: DocumentViewerController) {
    this.activeViewer.set(viewer);
  }

  unregister(viewer: DocumentViewerController) {
    if (this.activeViewer() === viewer) {
      this.activeViewer.set(undefined);
    }
  }
}
